import { DateField } from '~/common/components';
import { useI18n, Trans } from '~/common/context';
import { zodResolver } from '@hookform/resolvers/zod';
import { ValidationErrorResponse } from '@wyz/types';
import {
  Card,
  CenteredSpinner,
  Col,
  FormContainer,
  Row,
  Title,
  notify,
} from '@wyz/ui';
import { DateUtils } from '@wyz/utils';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import {
  usePricingPolicyErrorTranslation,
  useValidationForm,
} from '~/common/hooks';
import { CommonErrorCodesEnumType } from '~/common/types';
import { useManageQuoteServicePricingPolicies } from '~/purchase/api';
import { useQuoteServicePricingPolicyForm } from '~/purchase/components';
import { QuoteServicePricingPolicyHelpers } from '~/purchase/helpers';
import {
  PRICING_STATUS_ENUM,
  quoteServicePricingApplicationStepFormSchema,
} from '~/purchase/schemas';
import {
  IGlobalQuoteServicePricingFormData,
  IPricingStatus,
  IQuoteServicePricingApplicationStepForm,
} from '~/purchase/types';

type CommonFormProps = {
  globalFormData: IGlobalQuoteServicePricingFormData;
  customerId: string;
  policyGroupId?: number;
};

type ApplicationStepFormProps = CommonFormProps & {
  onSubmit: (
    data: IQuoteServicePricingApplicationStepForm,
    status: IPricingStatus,
  ) => void;
  errors?: null | ValidationErrorResponse<CommonErrorCodesEnumType>;
};

const ApplicationStepForm: React.FC<ApplicationStepFormProps> = ({
  onSubmit,
  customerId,
  globalFormData,
  policyGroupId,
  errors,
}) => {
  const { t } = useI18n();
  const { resolveErrorKey } = usePricingPolicyErrorTranslation();
  const manageServicePricingPolicies = useManageQuoteServicePricingPolicies({
    checkOverlap: { enabled: true },
  });
  const methods = useForm<IQuoteServicePricingApplicationStepForm>({
    defaultValues: globalFormData.APPLICATION,
    resolver: zodResolver(quoteServicePricingApplicationStepFormSchema),
  });
  useValidationForm({
    methods,
    errors: errors?.errors,
    t,
  });
  const pricingPolicyForm = useQuoteServicePricingPolicyForm();
  return (
    <FormContainer
      methods={methods}
      submitHandler={(formData, event) => {
        manageServicePricingPolicies.checkOverlap.mutate(
          {
            customerIds: [customerId],
            startDate: DateUtils.formatTimestamp(formData.startDate, {
              customFormat: DateUtils.CUSTOM_DATE_FORMAT.DMY,
            }),
            endDate: formData.endDate
              ? DateUtils.formatTimestamp(formData.endDate, {
                  customFormat: DateUtils.CUSTOM_DATE_FORMAT.DMY,
                })
              : null,
            policyGroupId: policyGroupId,
            default: false,
          },
          {
            onError: (error) => {
              notify.error(<Trans i18nKey={resolveErrorKey(error.message)} />, {
                id: 'toast-error-quote-service-pricing-policy-overlap-check',
              });
            },
            onSuccess: () => {
              onSubmit(
                formData,
                ((event?.nativeEvent as SubmitEvent)?.submitter?.getAttribute(
                  'data-status',
                ) as IPricingStatus) ?? PRICING_STATUS_ENUM.DRAFT,
              );
            },
          },
        );
      }}
      formConfiguration={{ mode: pricingPolicyForm.mode }}
      id={QuoteServicePricingPolicyHelpers.SERVICE_PRICING_POLICY_STEP_INDEX.APPLICATION.toString()}
    >
      <Card>
        {manageServicePricingPolicies.checkOverlap.isLoading && (
          <div
            className='position-absolute h-100 w-100'
            style={{ backgroundColor: 'rgba(0,0,0,.1)', zIndex: 5 }}
          >
            <div className='center-v-h'>
              <CenteredSpinner />
            </div>
          </div>
        )}
        <Card.Header>
          <Card.Title>
            <Title heading='xl' weight='bold'>
              <Trans
                i18nKey='app.service.pricing_policy.form.application.title'
                values={{
                  name: globalFormData.MOUNTING?.name,
                }}
              />
            </Title>
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col>
              <DateField
                name='startDate'
                label={<Trans i18nKey='app.common.start_date' />}
                placeholderText='dd-MM-yyyy'
                dateFormat={DateUtils.DATE_FORMAT.DMY}
                required
                showTimeSelect={false}
                translations={{
                  applyButton: t('app.common.button.apply'),
                  resetButton: t('app.common.button.reset'),
                }}
              />
            </Col>
            <Col>
              <DateField
                name='endDate'
                label={<Trans i18nKey='app.common.end_date' />}
                dateFormat={DateUtils.DATE_FORMAT.DMY}
                placeholderText='dd-MM-yyyy'
                showTimeSelect={false}
                translations={{
                  applyButton: t('app.common.button.apply'),
                  resetButton: t('app.common.button.reset'),
                }}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </FormContainer>
  );
};

export default ApplicationStepForm;
